/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { agentUpdateConfiguration } from "../funcs/agentUpdateConfiguration.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { DeviceAgentConfiguration } from "../models/components/deviceagentconfiguration.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateDeviceAgentConfigurationRequest,
  UpdateDeviceAgentConfigurationSecurity,
} from "../models/operations/updatedeviceagentconfiguration.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateDeviceAgentConfigurationMutationVariables = {
  request: UpdateDeviceAgentConfigurationRequest;
  security?: UpdateDeviceAgentConfigurationSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateDeviceAgentConfigurationMutationData =
  DeviceAgentConfiguration;

export type UpdateDeviceAgentConfigurationMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateConfiguration agent
 *
 * @remarks
 * Create or replace the organization-wide, non-secret device-agent configuration. Requires a session with the org:admin scope. Known settings are replaced wholesale — omitting one removes it — while stored keys this server does not recognize are preserved for forward compatibility; identity and credential keys are rejected.
 */
export function useUpdateDeviceAgentConfigurationMutation(
  options?: MutationHookOptions<
    UpdateDeviceAgentConfigurationMutationData,
    UpdateDeviceAgentConfigurationMutationError,
    UpdateDeviceAgentConfigurationMutationVariables
  >,
): UseMutationResult<
  UpdateDeviceAgentConfigurationMutationData,
  UpdateDeviceAgentConfigurationMutationError,
  UpdateDeviceAgentConfigurationMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateDeviceAgentConfigurationMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateDeviceAgentConfiguration(): MutationKey {
  return ["@gram/client", "agent", "updateConfiguration"];
}

export function buildUpdateDeviceAgentConfigurationMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateDeviceAgentConfigurationMutationVariables,
  ) => Promise<UpdateDeviceAgentConfigurationMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateDeviceAgentConfiguration(),
    mutationFn: function updateDeviceAgentConfigurationMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateDeviceAgentConfigurationMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(agentUpdateConfiguration(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
